// Template padrao geral e por loja (marketplace).

import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import type { StorySettings } from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useSaveStorySettings, type CustomStoryTemplate } from "@/lib/hooks/useStories";
import { TemplateOptions } from "./StoryGenerator";

export const STORE_OPTIONS: Array<{ code: string; label: string }> = [
  { code: "shopee", label: "Shopee" },
  { code: "amazon", label: "Amazon" },
  { code: "mercadolivre", label: "Mercado Livre" },
  { code: "magalu", label: "Magalu" },
  { code: "aliexpress", label: "AliExpress" },
  { code: "shein", label: "SHEIN" },
];

const SELECT_CLASS = "h-9 w-full rounded-md border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface-raised))] px-3 text-sm";

/** Mapa por loja sem entradas vazias. */
export function cleanByProvider(map: Readonly<Record<string, string>>): Record<string, string> {
  return Object.fromEntries(Object.entries(map).filter(([, ref]) => ref.length > 0));
}

interface Props {
  readonly settings: StorySettings;
  readonly customTemplates: readonly CustomStoryTemplate[];
  readonly onMessage: (message: string, ok: boolean) => void;
}

export function StoryDefaults({ settings, customTemplates, onMessage }: Props): JSX.Element {
  const save = useSaveStorySettings();
  const [draft, setDraft] = useState<StorySettings>(settings);
  useEffect(() => setDraft(settings), [settings]);

  async function handleSave(): Promise<void> {
    try {
      await save.mutateAsync({ defaultRef: draft.defaultRef, byProvider: cleanByProvider(draft.byProvider) });
      onMessage("Templates padrão salvos.", true);
    } catch (e) {
      onMessage(e instanceof Error ? e.message : "Não foi possível salvar.", false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="story-default" variant="field">Padrão geral</Label>
        <select id="story-default" className={SELECT_CLASS} value={draft.defaultRef ?? ""} onChange={(e) => setDraft({ ...draft, defaultRef: e.target.value || null })}>
          <option value="">Clássico (padrão do LucraLink)</option>
          <TemplateOptions customTemplates={customTemplates} />
        </select>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {STORE_OPTIONS.map((store) => (
          <div key={store.code} className="space-y-1.5">
            <Label htmlFor={`story-store-${store.code}`} variant="field">{store.label}</Label>
            <select
              id={`story-store-${store.code}`}
              className={SELECT_CLASS}
              value={draft.byProvider[store.code] ?? ""}
              onChange={(e) => setDraft({ ...draft, byProvider: { ...draft.byProvider, [store.code]: e.target.value } })}
            >
              <option value="">Usar o padrão geral</option>
              <TemplateOptions customTemplates={customTemplates} />
            </select>
          </div>
        ))}
      </div>
      <Button type="button" disabled={save.isPending} onClick={() => void handleSave()}>
        {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar padrões
      </Button>
    </div>
  );
}
